// Read-only adapter for the classic same-origin nested TradingView frame.
// Never intercept input, patch the engine, or inspect text fields/drawing contents.
export function observeLegacyFrames(root, runtime) {
  const documents = new Map();
  let frameState = '';
  const safely = fn => (...args) => { try { fn(...args); } catch (_) { /* Cross-origin/removed frame. */ } };
  const controls = {
    '#bot_borrador': 'erase-drawings',
    '[id^="bot_black_bar_color"]': 'bar-colors', '#bot_volumen': 'volume',
    '.undo': 'undo', '.redo': 'redo', '.legacy-settings-trigger': 'settings',
    '.chart-style-widget': 'chart-style', '.button.properties': 'chart-settings', '#legacy-chart-properties': 'chart-settings',
    '#legacy-chart-reset-view': 'reset-view', '.legacy-legend-ticker': 'ticker-search',
    '.tv-side-toolbar': 'drawing-tools'
  };
  function attach(doc) {
    if (!doc || documents.has(doc)) return;
    const cleanup = [];
    function listen(type, fn) {
      doc.addEventListener(type, fn, { capture: true, passive: true });
      cleanup.push(() => doc.removeEventListener(type, fn, true));
    }
    ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'].forEach(type => listen(type, runtime.activity));
    let lastGesture = 0;
    listen('wheel', safely(() => {
      if (Date.now() - lastGesture < 1500) return;
      lastGesture = Date.now(); runtime.track('interaction', 'chart', 'zoom-scroll', { input: 'wheel' });
    }));
    listen('pointerup', safely(event => {
      if (event.target.closest && event.target.closest('canvas')) runtime.track('interaction', 'chart', 'canvas', { input: 'pointer' });
    }));
    listen('click', safely(event => {
      if (!event.target.closest) return;
      const method = event.target.closest('[data-method]');
      const period = event.target.closest('[data-period]');
      let tool = method ? `overlay.${method.dataset.method}` : period ? `period.${period.dataset.period}` : '';
      if (!tool) tool = Object.keys(controls).filter(selector => event.target.closest(selector)).map(selector => controls[selector])[0];
      if (tool) runtime.track('interaction', 'chart', tool, { input: 'click' });
    }));
    listen('change', safely(event => {
      if (event.target.matches && event.target.matches('.legacy-bar-preset')) runtime.track('interaction', 'chart', 'bar-colors', { input: 'change' });
    }));
    documents.set(doc, () => cleanup.forEach(fn => fn()));
  }
  function scan() {
    const live = new Set();
    const visit = safely((parent, depth) => {
      if (depth > 2) return;
      parent.querySelectorAll('iframe').forEach(safely(frame => {
        const doc = frame.contentDocument;
        if (!doc) return;
        live.add(doc); attach(doc); visit(doc, depth + 1);
        const win = frame.contentWindow;
        if (win.stockchart && win.stockchart.s) {
          const state = { ticker: String(win.stockchart.s).slice(0, 64), volume: Boolean(win.volumenOn),
            bar_colors: Boolean(win.changesBarColorOn), overlays: {
              resistance: Boolean(win.resistenciaOn), support: Boolean(win.soporteOn), stop: Boolean(win.stopLossOn),
              lt: Boolean(win.lineaLTOn), pointLT: Boolean(win.puntoLTOn), pointRR: Boolean(win.puntoRROn)
            } };
          const period = doc.querySelector('iframe');
          const selected = period && period.contentDocument && period.contentDocument.querySelector('[data-period][aria-pressed="true"]');
          if (selected) state.resolution = selected.dataset.period;
          const engine = period && period.contentWindow;
          // Pinned classic engine read API, also used by the existing QA harness.
          try {
            const interval = engine._45.interval.value().replace(/^1/, '');
            if (['D', 'W', 'M'].includes(interval)) state.resolution = interval;
            const style = engine.Q3.model().model().properties().mainSeriesProperties.style.value();
            if (Number.isInteger(style) && style >= 0 && style <= 20) state.chart_type = style;
          } catch (_) { /* Older engine builds still expose the toolbar state. */ }
          const signature = JSON.stringify(state);
          if (signature !== frameState) { frameState = signature; runtime.track('state', 'chart', '', state); }
        }
      }));
    });
    visit(typeof root === 'function' ? root() : root, 0);
    documents.forEach((cleanup, doc) => { if (!live.has(doc)) { cleanup(); documents.delete(doc); } });
  }
  scan();
  const timer = setInterval(scan, 2000);
  return () => { clearInterval(timer); documents.forEach(cleanup => cleanup()); documents.clear(); };
}
